/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import DeviceChips from '@ion/applications/src/pages/devices/DeviceChips';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {DeviceBulkEditorFormFields} from '@ion/core/src/types/editors';
import {
  Account,
  Device,
  Devices,
  Entity
} from '@ion/core/src/types/entities';
import {toISOString} from '@ion/core/src/utils/DateUtil';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import DeviceConfigurationChooser from '@ion/features/src/entities/device/DeviceConfigurationChooser';
import DeviceTypeChooser from '@ion/features/src/entities/device/DeviceTypeChooser';
import CloseButton from '../components/buttons/CloseButton';
import FormGroupFields from '../components/forms/FormGroupFields';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useFormSave} from '../hooks/useFormSave';
import {useFormState} from '../hooks/useFormState';
import {useHasChange} from '../hooks/useHasChange';
import {useMergeState} from '../hooks/useMergeState';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsColor} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';
import {DLSTheme, IconSize} from '../types/types';
import CancelButton from './controls/CancelButton';
import SaveButton from './controls/SaveButton';
import DatePicker from './datetimepicker/DatePickerExporter';
import {
  getInitialState,
  isSCITemperaturOrMovingDevice,
  serialize
} from './BulkDeviceEditorUtils';
import {SCIDeviceSettingsFormField} from './DeviceEditorUtils';
import styles, {getClasses} from './editorStyles';
import EntityStatusChooser from './EntityStatusChooser';
import GroupChooser from './GroupChooser';
import ReportingRateChooser from './ReportingRateChooser';

// defines the prop definitions for bulk device editor ...
export interface BulkDeviceEditorProps {
  open: boolean;
  devices: Devices;
  account: Account;
  clear?: boolean;
  loading?: boolean;
  includeReportingSection?: boolean;
  includeDeviceType?: boolean;
  onSave: (devices: Devices) => void;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
};

interface TargetType {
  name: DeviceBulkEditorFormFields,
  value: any;
  required?: boolean;
}

const styleOverrides = (theme: DLSTheme) => ({
  paperScrollPaper : {
    width                                   : '100%',
    // a couple of devices' worth of content sits right at editorStyles.ts's
    // shared calc(100% - 94px) ceiling - close enough that Windows' slightly
    // taller font-metric line-height for the same text (vs macOS) tips the
    // inner `form`'s overflow-y:auto into showing a scrollbar there, while
    // macOS's overlay scrollbars hide that same sliver of overflow unless
    // actively scrolling. A little extra headroom, only here (not the
    // shared base every other editor dialog uses), keeps it from tipping
    // over on Windows without visibly changing the dialog's margin -
    // current theme only, legacy keeps the shared base's original ceiling ...
    maxHeight                               : toggleThemeStyle('calc(100% - 94px)', 'calc(100% - 80px)'),
    [theme.breakpoints.between(1024, 1200)] : {
      width : '86%'
    }
  },
  chipContainer : {
    display  : 'flex',
    flexWrap : 'wrap'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  },
  targetAccount : {
    marginTop : '-50px'
  }
});

/**
 *
 * The Bulk Device Form with set fields and encapsulated logic for handling the form's state,
 * including validation.
 */
export const BulkDeviceEditor = ({
  open,
  devices,
  account,
  onSave,
  onCancel,
  clear = false,
  loading = false,
  includeDeviceType = false,
  includeReportingSection = false
}: BulkDeviceEditorProps) => {
  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();

  const editorStyles = useExtendStyles((theme) =>
    styles(theme, isCurrentTheme),
  styleOverrides
  );

  const classes                               = editorStyles();
  const [openDatePickers, setOpenDatePickers] = useMergeState();
  const [formState, setFormState]             = useFormState(getInitialState({}, devices));
  const hasChange                             = useHasChange(formState);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const showReportingRateSection = useMemo(() => (
    includeReportingSection && isSCITemperaturOrMovingDevice(devices)
  ), [devices, includeReportingSection]);

  useEffect(() => {
    if (!isObjectEmpty(devices) && open) {
      setSelectedDevices(devices);
    }
  }, [open, devices]);

  const submitValidation = {
    [DeviceBulkEditorFormFields.DeviceConfiguration] : ({value}) => {
      const deviceType = formState[DeviceBulkEditorFormFields.DeviceType]?.value;
      return deviceType ? !!(deviceType && value) : true;
    }
  };

  const onSubmit = useFormSave({
    validate : submitValidation,
    serialize,
    formState,
    setFormState,
    onSave
  });

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  function clearForm() {
    setFormState({}, true);
  };

  const handleChooserChange = useCallback(
    ({name, value, required = false}: TargetType) => {
      setFormState({
        [name] : {
          name,
          value : name === DeviceBulkEditorFormFields.ShipDate
            ? toISOString(value)
            : isValue(value, 'none') ? null : value,
          required
        }
      });
    }, []);

  const onGroupChooserChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceBulkEditorFormFields.Groups,
      value
    });
  }, []);

  const onEntityStatusChooserChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceBulkEditorFormFields.Status,
      value
    });
  }, []);

  const onDeviceTypeChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceBulkEditorFormFields.DeviceType,
      value
    });
  }, []);

  function onDeviceConfigurationChange(value) {
    handleChooserChange({
      name     : DeviceBulkEditorFormFields.DeviceConfiguration,
      required : !!formState[DeviceBulkEditorFormFields.DeviceType]?.value,
      value
    });
  };

  const onShipDateChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceBulkEditorFormFields.ShipDate,
      value
    });
  }, []);

  const onToggleDatePicker = useCallback(() => {
    setOpenDatePickers(
      {[DeviceBulkEditorFormFields.ShipDate]: !openDatePickers[DeviceBulkEditorFormFields.ShipDate]}
    );
  }, [openDatePickers]);

  const onChipDelete = useCallback((selectedItem, selectedDevices) => {
    const filteredDevices = selectedDevices.filter((device) => {
      return device.id !== selectedItem.id;
    });
    setSelectedDevices(filteredDevices);
    setFormState({items: filteredDevices});
  }, []);

  const onDelete = useCallback(
    (selected: Device) => () => {
      onChipDelete(selected, selectedDevices);
    }, [selectedDevices]);

  const getSwitchLabel = useCallback((entry: string) => translate(`admin-devices:editors.${entry}.label`), []);

  return (
    <Dialog open={open}
      onBackdropClick={null}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <Render.If condition={isLegacyTheme}>
            <div onClick={onCancel} className={classes.closeButton}>
              <MaterialExitCloseIcon size={IconSize.medium}
                color={dlsColor('grey3')} />
            </div>
          </Render.If>
          <Typography component='h1' variant='h2'>
            {translate('common:editors.bulkEdit.title', {
              count  : selectedDevices?.length,
              entity : translate('common:entityLabels.devices')
            })}
          </Typography>
          <Render.If condition={isCurrentTheme}>
            <div className={classes.currentCloseButton}>
              <CloseButton onClick={onCancel} />
            </div>
          </Render.If>
        </div>
      </DialogTitle>
      <form className={classes.form} onSubmit={onSubmit}>
        <div className={classes.formWrapper}>
          <DialogContent className={classes.formDialogContent}>
            <FormSection title={includeDeviceType && translate('admin-devices:form.move.selected')}>
              <FormGroupFields>
                {includeDeviceType && (
                  <FormItem fullWidth>
                    <div className={classes.chipContainer}>
                      <DeviceChips devices={selectedDevices}
                        chipWrapperClass={classes.chipWrapper}
                        onDelete={onDelete} />
                    </div>
                  </FormItem>
                )}
                {includeDeviceType && (
                  <>
                    <FormItem>
                      <DeviceTypeChooser hasError={formState[DeviceBulkEditorFormFields.DeviceType]?.error}
                        onChange={onDeviceTypeChange} />
                    </FormItem>
                    <FormItem>
                      <DeviceConfigurationChooser isCtcAdmin={includeDeviceType}
                        accountId={entityId(account)}
                        deviceType={formState[DeviceBulkEditorFormFields.DeviceType]?.value}
                        onChange={onDeviceConfigurationChange}
                        helperText={formState[DeviceBulkEditorFormFields.DeviceConfiguration]?.error && translate('common:editors.shared.required')}
                        hasError={formState[DeviceBulkEditorFormFields.DeviceConfiguration]?.error} />
                    </FormItem>
                    <FormItem>
                      <DatePicker closeOnClickOutside
                        useDatePickerTextField
                        fieldLabel={translate('admin-devices:dialog.shipDate.label')}
                        placeholder={translate('admin-devices:dialog.shipDate.placeholder')}
                        open={openDatePickers[DeviceBulkEditorFormFields.ShipDate]}
                        toggle={onToggleDatePicker}
                        onChange={onShipDateChange} />
                    </FormItem>
                  </>
                )}
                {!includeDeviceType && (
                  <FormItem halfWidth>
                    <GroupChooser account={account}
                      onChange={onGroupChooserChange}
                      hasError={formState[DeviceBulkEditorFormFields.Groups]?.error} />
                  </FormItem>
                )}
                <FormItem halfWidth>
                  <EntityStatusChooser entity={Entity.device}
                    name={DeviceBulkEditorFormFields.Status}
                    onChange={onEntityStatusChooserChange} />
                </FormItem>
              </FormGroupFields>
            </FormSection>
            {showReportingRateSection && (
              <FormSection bottomBorder
                title={translate('admin-devices:editors.reportingRate.label')}>
                <FormGroupFields>
                  <FormItem>
                    <ReportingRateChooser fieldLabel={getSwitchLabel(
                      'reportingRateChooser.whenInMotion'
                    )}
                    readOnly={loading}
                    rate={formState[SCIDeviceSettingsFormField.MoveWakeupPeriod]?.value}
                    name={SCIDeviceSettingsFormField.MoveWakeupPeriod}
                    onChange={handleChooserChange}
                    fallbackValue={false} />
                  </FormItem>
                  <FormItem>
                    <ReportingRateChooser fieldLabel={getSwitchLabel(
                      'reportingRateChooser.whenInStationary'
                    )}
                    readOnly={loading}
                    rate={formState[SCIDeviceSettingsFormField.WakeupPeriod]?.value}
                    name={SCIDeviceSettingsFormField.WakeupPeriod}
                    onChange={handleChooserChange}
                    fallbackValue={false} />
                  </FormItem>
                  <FormItem>
                    <ReportingRateChooser fieldLabel={getSwitchLabel(
                      'reportingRateChooser.setTemperatureLogging'
                    )}
                    readOnly={loading}
                    rate={formState[SCIDeviceSettingsFormField.SensorLoggingPeriod]?.value}
                    name={SCIDeviceSettingsFormField.SensorLoggingPeriod}
                    onChange={handleChooserChange}
                    fallbackValue={false} />
                  </FormItem>
                </FormGroupFields>
              </FormSection>
            )}
          </DialogContent>
        </div>
      </form>
      <DialogActions className={classes.formActions}>
        <CancelButton onCancel={onCancel}
          clearForm={clearForm}
          disabled={loading} />
        <SaveButton onSave={onSubmit}
          disabled={loading || !hasChange}
          loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeviceEditor;
